import type { ComponentChildren } from "preact";
import { createContext } from "preact";
import { useContext, useState } from "preact/hooks";
import { createBuildWorkerBridge } from "@brickme/frontend-common/src/picture-build/build-worker-bridge.ts";
import type { BuildWorker } from "@brickme/frontend-common/src/picture-build/build-worker-bridge.ts";

type ContextValue = {
	readonly buildWorker: BuildWorker;
};

const context = createContext<ContextValue | undefined>(undefined);

type WorkersContextProviderProps = {
	readonly children: ComponentChildren;
};

// TODO: I think we should *probably* remove this. Build script is very light now.
// Plus trying to re-use build worker is error prone when switching projects.
// Purpose of this is to load workers from the start. Otherwise there's a pause when
// show editor for the first time. Mainly due to opencv being a large download.
// No longer relevant since open cv gone, but still good to preload.
function WorkersProvider({ children }: WorkersContextProviderProps) {
	// Note: https://stackoverflow.com/a/72681399/7743183
	// you should never start more Workers than navigator.hardwareConcurrency - 1.
	// Failing to do so, your Workers's concurrency would be done through task-switching
	// instead of true parallelism, and that would incur a significant performance penalty.
	// Daniel: In reality, nearly all users will have multi-core these days. We have a
	// main thread renderer so do check concurrency there.
	const [value] = useState(() => ({
		buildWorker: createBuildWorkerBridge(),
	}));
	return <context.Provider value={value}>{children}</context.Provider>;
}

function useBuildWorker() {
	const value = useContext(context);
	if (value === undefined) {
		throw new Error("No worker context");
	}
	return value.buildWorker;
}

export { useBuildWorker, WorkersProvider };
